import type { CSSProperties } from 'react';
import type { RpPageTheme } from '@mlm/shared';
import { RP_RADIUS_CSS } from './tokens';

/**
 * A page theme → inline CSS custom properties for the page's root wrapper.
 *
 * Scoped to a SUBTREE on purpose — this is the `.omni-store` scoping trick made
 * data-driven. `useTenantTheme` sets the tenant's tokens as inline styles on
 * `<html>`; setting page tokens there too would reskin the whole member SPA and
 * leak across route changes. Setting them on the wrapper means:
 *
 *   :root (tenant theme)  →  wrapper (this page's overrides)  →  nothing else
 *
 * An OMITTED token is simply absent from the returned object, so it inherits the
 * tenant theme untouched — which is why an un-themed page looks exactly like the
 * member app does today.
 */
export function rpThemeVars(theme: RpPageTheme | undefined): CSSProperties {
  if (!theme) return {};
  const v: Record<string, string> = {};
  if (theme.accent) v['--primary'] = theme.accent;
  if (theme.accentFg) v['--primary-foreground'] = theme.accentFg;
  if (theme.pageBg) v['--background'] = theme.pageBg;
  if (theme.pageFg) v['--foreground'] = theme.pageFg;
  if (theme.cardBg) v['--card'] = theme.cardBg;
  if (theme.mutedBg) v['--muted'] = theme.mutedBg;
  if (theme.radius) v['--radius'] = RP_RADIUS_CSS[theme.radius] ?? RP_RADIUS_CSS.md;
  if (theme.fontHeading) v['--font-heading'] = theme.fontHeading;
  if (theme.fontBody) v['--font-body'] = theme.fontBody;
  // Cast: CSSProperties has no index signature for custom properties, but React
  // passes `--*` keys through to the DOM verbatim.
  return v as CSSProperties;
}

/**
 * Inject a page theme's stylesheet links (web fonts) and remove them on unmount.
 * Mirrors `useTenantTheme`'s `data-tenant-font` handling. Idempotent per href,
 * so two pages naming the same font don't stack duplicate `<link>`s.
 */
export function applyRpFontLinks(hrefs: readonly string[] | undefined): () => void {
  if (typeof document === 'undefined' || !hrefs?.length) return () => {};
  const added: HTMLLinkElement[] = [];
  for (const href of hrefs) {
    if (document.querySelector(`link[data-rp-font="${CSS.escape(href)}"]`)) continue;
    const el = document.createElement('link');
    el.rel = 'stylesheet';
    el.href = href;
    el.dataset.rpFont = href;
    document.head.appendChild(el);
    added.push(el);
  }
  return () => added.forEach((el) => el.remove());
}
